import React from "react";
import { useTranslation } from "react-i18next";
import { Grid2, Typography } from "@mui/material";
import { toCurrency, toCurrencyWholeNumber } from "@helpers";
import CoinsGrowthIcon from "@icons/myicons/coins-growth-2.svg?react";
import { InfoAccordion } from "../components";
import { AccordionSummaryHeader } from "./AccordionSummaryHeader";
import type { GainLossValues } from "../helpers";

interface Props {
  values: GainLossValues;
  currency: string;
}

export const RealizedGainsAccordion = ({ values, currency }: Props) => {
  const { t } = useTranslation();
  const labelColor = "textSecondary";

  return (
    <InfoAccordion
      id="realized-gains"
      expandIconTestId="expand-realized-gains"
      summary={
        <AccordionSummaryHeader
          icon={<CoinsGrowthIcon width={16} height={16} />}
          label={t("investment.details.gainLossStatistics.realizedGains")}
          primaryValue={
            <Typography
              variant="body2"
              data-testid="gainLossStatistics.netTotalGain"
            >
              {toCurrency(values.netTotalGain, currency)}
            </Typography>
          }
        />
      }
    >
      <Grid2 container rowSpacing={3} columnSpacing={2} mt={3}>
        <Grid2 size={{ xs: 7 }}>
          <Typography color={labelColor} variant="body3">
            {t("investment.details.gainLossStatistics.tradingGains")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 5 }}>
          <Typography
            color="textPrimary"
            variant="body4"
            data-testid="gainLossStatistics.grossTradingGain"
          >
            {toCurrencyWholeNumber(values.grossTradingGain, currency)}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 7 }}>
          <Typography color={labelColor} variant="body3">
            {t("investment.details.gainLossStatistics.dividends")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 5 }}>
          <Typography
            color="textPrimary"
            variant="body4"
            data-testid="gainLossStatistics.dividends"
          >
            {toCurrencyWholeNumber(values.grossDividendGain, currency)}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 7 }}>
          <Typography color={labelColor} variant="body3">
            {t("investment.details.gainLossStatistics.commission")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 5 }}>
          <Typography
            color="textPrimary"
            variant="body4"
            data-testid="gainLossStatistics.commission"
          >
            {toCurrencyWholeNumber(values.commission, currency)}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 7 }}>
          <Typography color={labelColor} variant="body3">
            {t("investment.details.gainLossStatistics.tax")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 5 }}>
          <Typography
            color="textPrimary"
            variant="body4"
            data-testid="gainLossStatistics.taxAmount"
          >
            {toCurrencyWholeNumber(values.taxAmount, currency)}
          </Typography>
        </Grid2>
      </Grid2>
    </InfoAccordion>
  );
};
